import React, { useEffect, useRef, useState } from "react";
import StandardTemplate from "../../../templates/standard_template/standard_template";
import OpenHouseFlyer from "../../../components/loan_tools/forms/open_house_flyer";
import { useDocument } from "../../../components/loan_tools/loan_tool";

// Downscale + JPEG-compress an uploaded image before it is saved into the loan
// file, so a photo library never bloats the document. Longest side is capped and
// quality reduced; a typical listing photo lands around 100-250 KB.
function compressImage(file, maxDim = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = window.document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OpenHouseFlyerRoute() {
  const [loanDoc, setDocument, , actions] = useDocument();
  const [pdfBusy, setPdfBusy] = useState(false);
  const fileRef = useRef();

  // Photo library lives on the loan file so it persists and survives reloads:
  //   loanDoc.flyer_photos          = [{ id, label, dataUrl }]  (labeled listings)
  //   loanDoc.flyer_selected_photo_id = which one is placed on the flyer
  const photos = (loanDoc && loanDoc.flyer_photos) || [];
  const selectedId =
    (loanDoc && loanDoc.flyer_selected_photo_id) ||
    (photos[0] && photos[0].id) ||
    "";
  const selectedPhoto = photos.find(p => p.id === selectedId) || null;
  const homePhoto = selectedPhoto ? selectedPhoto.dataUrl : null;

  function persist(nextDoc) {
    setDocument(nextDoc);
    try {
      actions.saveDocument();
    } catch (e) {
      // non-fatal; the change stays on screen
    }
  }

  async function onPickPhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // let the LO re-pick the same file later
    if (!file) return;
    // "Save As" — name the photo (default to the subject property address so a
    // Realtor's multiple listings are easy to tell apart).
    const defaultLabel = (loanDoc.property && loanDoc.property.address) || "";
    const label = window.prompt(
      "Name this photo (e.g., the property address):",
      defaultLabel
    );
    if (label === null) return; // cancelled
    let dataUrl;
    try {
      dataUrl = await compressImage(file);
    } catch (err) {
      window.alert("Sorry — couldn't read that image. Please try another file.");
      return;
    }
    const id = "p" + Date.now() + Math.random().toString(36).slice(2, 7);
    const newPhoto = { id, label: (label || "").trim() || "Untitled", dataUrl };
    persist({
      ...loanDoc,
      flyer_photos: [...photos, newPhoto],
      flyer_selected_photo_id: id
    });
  }

  function onSelectPhoto(e) {
    persist({ ...loanDoc, flyer_selected_photo_id: e.target.value });
  }

  function onRenamePhoto() {
    if (!selectedPhoto) return;
    const label = window.prompt("Rename this photo:", selectedPhoto.label);
    if (label === null) return;
    persist({
      ...loanDoc,
      flyer_photos: photos.map(p =>
        p.id === selectedId
          ? { ...p, label: (label || "").trim() || "Untitled" }
          : p
      )
    });
  }

  function onDeletePhoto() {
    if (!selectedPhoto) return;
    if (!window.confirm(`Delete photo "${selectedPhoto.label}"?`)) return;
    const nextPhotos = photos.filter(p => p.id !== selectedId);
    persist({
      ...loanDoc,
      flyer_photos: nextPhotos,
      flyer_selected_photo_id: (nextPhotos[0] && nextPhotos[0].id) || ""
    });
  }

  // Download the flyer as a real PDF file so it looks and prints IDENTICALLY on
  // every browser and printer (sidesteps Chrome's fit-to-printer scaling). Renders
  // the on-screen flyer at 3x for crisp text, then places it on a Letter page. The
  // libraries are code-split (loaded only on click) so they never bloat the app.
  async function downloadPdf() {
    const el = window.document.querySelector(".ohf-doc");
    if (!el) return;
    setPdfBusy(true);
    try {
      const [{ default: html2canvas }, jspdfModule] = await Promise.all([
        import("html2canvas"),
        import("jspdf")
      ]);
      const JsPdf = jspdfModule.jsPDF || jspdfModule.default;
      const canvas = await html2canvas(el, {
        scale: 3,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new JsPdf({
        unit: "in",
        format: "letter",
        orientation: "portrait"
      });
      const pageWidth = 8.5;
      const pageHeight = 11;
      const margin = 0.3;
      const availWidth = pageWidth - margin * 2;
      const availHeight = pageHeight - margin * 2;
      const ratio = canvas.width / canvas.height;
      let width = availWidth;
      let height = width / ratio;
      if (height > availHeight) {
        height = availHeight;
        width = height * ratio;
      }
      const x = (pageWidth - width) / 2;
      const y = (pageHeight - height) / 2;
      pdf.addImage(imgData, "JPEG", x, y, width, height);
      const borrowers =
        loanDoc && loanDoc.applicant && loanDoc.applicant.borrowers;
      const lastName =
        (borrowers && borrowers[0] && borrowers[0].last_name) || "Open House";
      pdf.save(`${lastName} Open House Flyer.pdf`);
    } catch (e) {
      window.alert(
        "Sorry — couldn't generate the PDF. You can still use Print Now and choose “Save as PDF.”"
      );
    } finally {
      setPdfBusy(false);
    }
  }

  useEffect(() => {
    const borrowers = loanDoc && loanDoc.applicant && loanDoc.applicant.borrowers;
    const lastName = (borrowers && borrowers[0] && borrowers[0].last_name) || "";
    const title = lastName ? `${lastName} – Open House Flyer` : "Open House Flyer";
    actions.setPageTitleOverride(title);
    return () => actions.setPageTitleOverride(null);
  }, [loanDoc]);

  return (
    <StandardTemplate className="ba-template">
      <div className="ba-page-actions no-print">
        <button
          className="button"
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          Upload Pic
        </button>
        {photos.length > 0 && (
          <>
            <select
              className="ohf-photo-picker"
              value={selectedId}
              onChange={onSelectPhoto}
              style={{ padding: "4px 8px", marginLeft: "4px" }}
            >
              {photos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <button className="button" onClick={onRenamePhoto}>
              Rename Pic
            </button>
            <button className="button" onClick={onDeletePhoto}>
              Delete Pic
            </button>
          </>
        )}
        <button className="button" onClick={() => window.print()}>
          Print Now
        </button>
        <button className="button" onClick={downloadPdf} disabled={pdfBusy}>
          {pdfBusy ? "Generating PDF…" : "Download PDF"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={onPickPhoto}
        />
      </div>
      <OpenHouseFlyer homePhoto={homePhoto} />
    </StandardTemplate>
  );
}
